import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { EditIcon, DeleteIcon, PauseIcon, PlayIcon, CheckIcon } from '../components/Icons';

const ALERT_TYPES = [
  { value: 'price_above', label: 'Price Above', hasThreshold: true, unit: '$' },
  { value: 'price_below', label: 'Price Below', hasThreshold: true, unit: '$' },
  { value: 'pct_move_up', label: '% Move Up', hasThreshold: true, unit: '%' },
  { value: 'pct_move_down', label: '% Move Down', hasThreshold: true, unit: '%' },
  { value: 'position_pct_change', label: 'Position % Change', hasThreshold: true, unit: '%' },
  { value: 'position_value_above', label: 'Position Value Above', hasThreshold: true, unit: '$' },
  { value: 'position_value_below', label: 'Position Value Below', hasThreshold: true, unit: '$' },
  { value: 'high_52w', label: '52-Week High', hasThreshold: false },
  { value: 'low_52w', label: '52-Week Low', hasThreshold: false },
  { value: 'volume_spike', label: 'Volume Spike', hasThreshold: true, unit: 'x avg' },
  { value: 'ma_cross_above', label: 'MA Cross Above', hasThreshold: true, unit: 'days' },
  { value: 'ma_cross_below', label: 'MA Cross Below', hasThreshold: true, unit: 'days' },
  { value: 'ma_turn_positive', label: 'MA Turns Positive', hasThreshold: true, unit: 'days' },
  { value: 'ma_turn_negative', label: 'MA Turns Negative', hasThreshold: true, unit: 'days' },
  { value: 'ma_cross_ma_above', label: 'MA Crosses Above MA', hasThreshold: false, isDualMa: true },
  { value: 'ma_cross_ma_below', label: 'MA Crosses Below MA', hasThreshold: false, isDualMa: true },
  { value: 'trade_pct_up', label: '% Up Since Last Trade', hasThreshold: true, unit: '%', isTradeRef: true },
  { value: 'trade_pct_down', label: '% Down Since Last Trade', hasThreshold: true, unit: '%', isTradeRef: true },
];

const EMPTY_FORM = {
  symbol: '', alert_type: 'price_above', threshold_value: '',
  ma1_type: 'EMA', ma1_period: 50, ma2_type: 'SMA', ma2_period: 200,
  account_id: '', trade_type: 'buy',  // '' account = all accounts
  notes: '',
};

export default function Alerts({ navParams = {}, onNavigate }) {
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('alerts');
  const [showModal, setShowModal] = useState(false);
  const [editAlert, setEditAlert] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertSort, setAlertSort] = useState({ field: 'symbol', dir: 'asc' });
  const [eventSort, setEventSort] = useState({ field: 'created_at', dir: 'desc' });
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // trade_pct_* only: the API's check of the trade the alert would measure from
  const [tradeRef, setTradeRef] = useState(null);
  const [accountNote, setAccountNote] = useState('');

  const load = () => Promise.all([api.getAlerts(), api.getAlertEvents(false)])
    .then(([a, e]) => {
      setAlerts(a);
      setEvents(e);
      const ids = new Set(e.map(ev => ev.id));
      setSelectedEvents(sel => sel.filter(id => ids.has(id)));
    });

  useEffect(() => { load(); }, []);
  useEffect(() => { api.getAccounts().then(setAccounts).catch(() => setAccounts([])); }, []);

  useEffect(() => {
    if (navParams.symbol) {
      setForm(f => ({ ...f, symbol: navParams.symbol }));
      setShowModal(true);
    }
  }, [navParams.symbol]);

  const selectedType = ALERT_TYPES.find(t => t.value === form.alert_type);
  const isTradeRef = !!selectedType?.isTradeRef;

  // An account switch made below stays noted until the user changes what's being measured
  useEffect(() => { setAccountNote(''); }, [form.symbol, form.alert_type, form.trade_type]);

  useEffect(() => {
    if (!showModal) setAccountNote('');
    if (!showModal || !isTradeRef || !form.symbol) { setTradeRef(null); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.getTradeReference({ symbol: form.symbol, trade_type: form.trade_type, account_id: form.account_id })
        .then(res => {
          if (cancelled) return;
          if (res.code === 'no_trade_in_account') {
            // The chosen account has no such trade but others do: measure across all of them
            setAccountNote(`Switched to All accounts. ${res.message}`);
            setForm(f => ({ ...f, account_id: '' }));
          } else {
            setTradeRef(res);
          }
        })
        .catch(() => { if (!cancelled) setTradeRef(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showModal, isTradeRef, form.symbol, form.trade_type, form.account_id]);

  const tradeRefBlocked = isTradeRef && tradeRef && tradeRef.code !== 'ok';
  const accountName = (id) => accounts.find(a => a.id === id)?.name || 'Unknown account';

  const openAdd = () => { setEditAlert(null); setForm(EMPTY_FORM); setError(''); setShowModal(true); };

  const openEdit = (a) => {
    setEditAlert(a);
    setForm({
      symbol: a.symbol,
      alert_type: a.alert_type,
      threshold_value: a.threshold_value ?? '',
      ma1_type: a.ma1_type || 'EMA',
      ma1_period: a.ma1_period ?? 50,
      ma2_type: a.ma2_type || 'SMA',
      ma2_period: a.ma2_period ?? 200,
      account_id: a.account_id ?? '',
      trade_type: a.trade_type || 'buy',
      notes: a.notes || ''
    });
    setError('');
    setShowModal(true);
  };

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      if (editAlert) {
        await api.updateAlert(editAlert.id, {
          alert_type: form.alert_type,
          threshold_value: selectedType?.hasThreshold ? parseFloat(form.threshold_value) : null,
          ...(selectedType?.isDualMa ? {
            ma1_type: form.ma1_type,
            ma1_period: parseInt(form.ma1_period, 10),
            ma2_type: form.ma2_type,
            ma2_period: parseInt(form.ma2_period, 10),
          } : {}),
          ...(isTradeRef ? {
            account_id: form.account_id === '' ? null : Number(form.account_id),
            trade_type: form.trade_type,
          } : {}),
          notes: form.notes || null,
        });
      } else {
        await api.createAlert({
          symbol: form.symbol.toUpperCase(),
          alert_type: form.alert_type,
          threshold_value: selectedType?.hasThreshold ? parseFloat(form.threshold_value) : null,
          ...(selectedType?.isDualMa ? {
            ma1_type: form.ma1_type,
            ma1_period: parseInt(form.ma1_period, 10),
            ma2_type: form.ma2_type,
            ma2_period: parseInt(form.ma2_period, 10),
          } : {}),
          ...(isTradeRef ? {
            account_id: form.account_id === '' ? null : Number(form.account_id),
            trade_type: form.trade_type,
          } : {}),
          notes: form.notes || null,
        });
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditAlert(null);
      load();
    } catch (e) {
      setError(e.detail?.message || 'Failed to save alert. Check symbol has price data and threshold is set.');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (alert) => {
    await api.updateAlert(alert.id, { is_active: !alert.is_active });
    load();
  };

  const remove = async (id) => {
    if (window.confirm('Delete this alert?')) {
      await api.deleteAlert(id);
      load();
    }
  };

const deleteEvent = async (id) => {
    if (window.confirm('Delete this triggered alert?')) {
      await api.deleteAlertEvent(id);
      load();
    }
  };

  const toggleSelectEvent = (id) => {
    setSelectedEvents(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };

  const allEventsSelected = events.length > 0 && selectedEvents.length === events.length;
  const toggleSelectAllEvents = () => {
    setSelectedEvents(allEventsSelected ? [] : events.map(e => e.id));
  };

  const deleteSelectedEvents = async () => {
    const n = selectedEvents.length;
    if (!window.confirm(`Delete ${n} triggered alert${n === 1 ? '' : 's'}?`)) return;
    await Promise.all(selectedEvents.map(id => api.deleteAlertEvent(id)));
    setSelectedEvents([]);
    load();
  };

  const markAllRead = async () => {
    await api.markAllEventsRead();
    load();
  };

  const fmt = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const unread = events.filter(e => !e.is_read).length;

  const toggleAlertSort = (field) => setAlertSort(s => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }));
  const toggleEventSort = (field) => setEventSort(s => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }));

  const sortRows = (rows, { field, dir }) => [...rows].sort((a, b) => {
    let av = a[field], bv = b[field];
    if (av == null) av = dir === 'asc' ? Infinity : -Infinity;
    if (bv == null) bv = dir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return dir === 'asc' ? av - bv : bv - av;
  });

  const SortTh = ({ field, label, onToggle, current }) => (
    <th onClick={() => onToggle(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {current.field === field ? (current.dir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const SymbolLink = ({ symbol }) => (
    <strong className="mono" style={{ cursor: 'pointer', color: 'var(--accent)' }}
      onClick={() => onNavigate && onNavigate('charts', { symbol })}>
      {symbol}
    </strong>
  );

    return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Price Alerts</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={() => { api.checkAlertsNow(); setTimeout(load, 2000); }}>Check Now</button>
          <button className="btn" onClick={openAdd}>+ New Alert</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className={tab === 'alerts' ? 'btn' : 'btn-ghost'} onClick={() => setTab('alerts')}>
          Active Alerts ({alerts.filter(a => a.is_active).length})
        </button>
        <button className={tab === 'history' ? 'btn' : 'btn-ghost'} onClick={() => setTab('history')}>
          Triggered {unread > 0 && <span style={{ marginLeft: 6, background: 'var(--loss)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{unread}</span>}
        </button>
      </div>

      {tab === 'alerts' && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <SortTh field="symbol" label="Symbol" onToggle={toggleAlertSort} current={alertSort} />
                <SortTh field="alert_type" label="Type" onToggle={toggleAlertSort} current={alertSort} />
                <SortTh field="threshold_value" label="Threshold" onToggle={toggleAlertSort} current={alertSort} />
                <SortTh field="reference_price" label="Reference" onToggle={toggleAlertSort} current={alertSort} />
                <SortTh field="last_triggered_at" label="Last Triggered" onToggle={toggleAlertSort} current={alertSort} />
                <SortTh field="is_active" label="Status" onToggle={toggleAlertSort} current={alertSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortRows(alerts, alertSort).map(a => {
                const type = ALERT_TYPES.find(t => t.value === a.alert_type);
                return (
                  <tr key={a.id} className={a.is_active ? undefined : 'row-dimmed'}>
                    <td><SymbolLink symbol={a.symbol} /></td>
                    <td>
                      {type?.label || a.alert_type}
                      {type?.isTradeRef && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          Last {a.trade_type} · {a.account_id == null ? 'All accounts' : accountName(a.account_id)}
                        </div>
                      )}
                    </td>
                    <td className="mono amount">
                      {type?.isDualMa && a.ma1_type
                        ? `${a.ma1_type}(${a.ma1_period}) / ${a.ma2_type}(${a.ma2_period})`
                        : a.threshold_value != null
                          ? (type?.unit === '$' ? fmt(a.threshold_value) : `${a.threshold_value}${type?.unit ? ' ' + type.unit : ''}`)
                          : '—'}
                    </td>
                    <td className="mono amount">
                      {type?.isTradeRef
                        ? (a.reference_trade ? fmt(a.reference_trade.price) : '—')
                        : (a.reference_price ? fmt(a.reference_price) : '—')}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {a.last_triggered_at ? new Date(a.last_triggered_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td>
                      <span className={a.is_active ? 'gain' : 'muted'} style={{ fontSize: 12 }}>
                        {a.is_active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="icon-btn" style={{ marginRight: 6 }} title={a.is_active ? 'Pause' : 'Resume'} aria-label={a.is_active ? 'Pause' : 'Resume'} onClick={() => toggleActive(a)}>
                        {a.is_active ? <PauseIcon /> : <PlayIcon />}
                      </button>
                      <button className="icon-btn" style={{ marginRight: 6 }} title="Edit" aria-label="Edit" onClick={() => openEdit(a)}><EditIcon /></button>
                      <button className="icon-btn" title="Delete" aria-label="Delete" onClick={() => remove(a.id)}><DeleteIcon /></button>
                    </td>
                  </tr>
                );
              })}
              {alerts.length === 0 && (
                <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>No alerts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          {(selectedEvents.length > 0 || unread > 0) && (
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {selectedEvents.length > 0 && (
                <button className="btn-ghost" onClick={deleteSelectedEvents}>Delete Selected ({selectedEvents.length})</button>
              )}
              {unread > 0 && (
                <button className="btn-ghost" onClick={markAllRead}>Mark all as read</button>
              )}
            </div>
          )}
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox" checked={allEventsSelected} onChange={toggleSelectAllEvents} style={{ width: 'auto' }} />
                </th>
                <SortTh field="created_at" label="Date" onToggle={toggleEventSort} current={eventSort} />
                <SortTh field="symbol" label="Symbol" onToggle={toggleEventSort} current={eventSort} />
                <SortTh field="message" label="Message" onToggle={toggleEventSort} current={eventSort} />
                <SortTh field="triggered_price" label="Price" onToggle={toggleEventSort} current={eventSort} />
                <SortTh field="is_read" label="Status" onToggle={toggleEventSort} current={eventSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortRows(events, eventSort).map(e => (
                <tr key={e.id} style={{ opacity: e.is_read ? 0.5 : 1 }}>
                  <td>
                    <input type="checkbox" checked={selectedEvents.includes(e.id)} onChange={() => toggleSelectEvent(e.id)} style={{ width: 'auto' }} />
                  </td>
                  <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td><SymbolLink symbol={e.symbol} /></td>
                  <td style={{ fontSize: 13 }}>{e.message}</td>
                  <td className="mono amount">{e.triggered_price ? fmt(e.triggered_price) : '—'}</td>
                  <td><span className={e.is_read ? 'muted' : 'gain'} style={{ fontSize: 12 }}>{e.is_read ? 'Read' : 'New'}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!e.is_read && (
                      <button className="icon-btn" style={{ marginRight: 6 }} title="Mark as read" aria-label="Mark as read" onClick={() => api.markEventRead(e.id).then(load)}><CheckIcon /></button>
                    )}
                    <button className="icon-btn" title="Delete" aria-label="Delete" onClick={() => deleteEvent(e.id)}><DeleteIcon /></button>
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>No alerts have triggered yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(showModal) && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditAlert(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{editAlert ? `Edit Alert — ${editAlert.symbol}` : 'New Price Alert'}</div>
            {!editAlert && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Symbol</label>
                  <input value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="AAPL" autoFocus />
                </div>
                <div className="form-group">
                  <label>Alert Type</label>
                  <select value={form.alert_type} onChange={e => setForm({ ...form, alert_type: e.target.value, threshold_value: '' })}>
                    {ALERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
            )}
            {editAlert && (
              <div className="form-group">
                <label>Alert Type</label>
                <select value={form.alert_type} onChange={e => setForm({ ...form, alert_type: e.target.value, threshold_value: '' })}>
                  {ALERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
            {selectedType?.hasThreshold && (
              <div className="form-group">
                <label>Threshold ({selectedType.unit})</label>
                <input type="number" value={form.threshold_value}
                  onChange={e => setForm({ ...form, threshold_value: e.target.value })}
                  placeholder={selectedType.unit === '$' ? '0.00' : selectedType.unit === '%' ? '5' : selectedType.unit === 'days' ? '50' : '2'} />
              </div>
            )}
            {isTradeRef && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Measure From</label>
                  <select value={form.trade_type} onChange={e => setForm({ ...form, trade_type: e.target.value })}>
                    <option value="buy">Last Buy</option>
                    <option value="sell">Last Sell</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Account</label>
                  <select value={form.account_id} onChange={e => { setAccountNote(''); setForm({ ...form, account_id: e.target.value }); }}>
                    <option value="">All accounts</option>
                    {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                  </select>
                </div>
              </div>
            )}
            {selectedType?.isDualMa && (
              <div className="form-group">
                <label>Moving Averages</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input type="number" min={1} max={500} value={form.ma1_period}
                    onChange={e => setForm({ ...form, ma1_period: e.target.value })} style={{ width: 80 }} />
                  <select value={form.ma1_type} onChange={e => setForm({ ...form, ma1_type: e.target.value })} style={{ width: 90 }}>
                    <option value="SMA">SMA</option>
                    <option value="EMA">EMA</option>
                  </select>
                </div>
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                  {selectedType.value === 'ma_cross_ma_above' ? 'crosses above' : 'crosses below'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" min={1} max={500} value={form.ma2_period}
                    onChange={e => setForm({ ...form, ma2_period: e.target.value })} style={{ width: 80 }} />
                  <select value={form.ma2_type} onChange={e => setForm({ ...form, ma2_type: e.target.value })} style={{ width: 90 }}>
                    <option value="SMA">SMA</option>
                    <option value="EMA">EMA</option>
                  </select>
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Notes (optional)</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. take profit target" />
            </div>
            {isTradeRef && accountNote && (
              <div style={{ fontSize: 13, marginBottom: 8 }}>{accountNote}</div>
            )}
            {isTradeRef && tradeRef && (tradeRef.code === 'ok' ? (
              <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                Measuring from the {tradeRef.trade.transaction_type} on {tradeRef.trade.date} at {fmt(tradeRef.trade.price)} in {tradeRef.trade.account_name}.
              </div>
            ) : (
              <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{tradeRef.message}</div>
            ))}
            {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => { setShowModal(false); setEditAlert(null); setError(''); }}>Cancel</button>
              <button className="btn" onClick={submit} disabled={loading || tradeRefBlocked}>{loading ? 'Saving...' : editAlert ? 'Save Changes' : 'Create Alert'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}